import { CLEAR_MODES, Filter, FilterSystem, Matrix, RenderTexture, Sprite, Texture, utils } from "pixi.js"
import { DayPhases } from "game-common/mechanics/physics_mechanics"
import { NightSky, Visibility } from "game-common/models"
import { moonShown } from "game-common/moon_cycle"

/**
 * How the night looks outdoors: lighter than the plain darkness it replaces, and made to read as night by COLOUR
 * rather than by brightness. Unlit ground goes cool and grey (eyes lose colour in the dark before they lose
 * brightness), its darkest art is lifted off black, and whatever a light reaches keeps its colour and warms. The
 * hearth is the one place that keeps the day's colours.
 *
 * `color` is the floor at the darkest of the night: what unlit ground is multiplied by. `saturation` is how much of
 * its colour unlit ground keeps there (1: all of it); `lift` is added to it there. Both grow in from dusk.
 * A moonless night is darker, colder and greyer, and has no moon shadows (building_shadow shadowLight). Between
 * the two is how much moon shows (moon_cycle.ts moonShown): the room's sky, less the rain clouds over it, so the
 * night fades with the clouds the rain draws. Caves, houses and dungeons (their own fixed darkness, no day cycle)
 * keep the plain darkness.
 *
 * Tune live: window.nightLook = { moonlit: { color, saturation, lift }, moonless: {...}, light } (any subset);
 * window.nightLook = false for the plain grey darkness. Which night a camp has: /ember night.
 */
export interface NightSkyLook { color: number; saturation: number; lift: number }

export const NIGHT_SKIES: Record<NightSky, NightSkyLook> = {
    moonlit: { color: 0x626f98, saturation: 0.45, lift: 0x0a0c16 },
    moonless: { color: 0x3a4766, saturation: 0.3, lift: 0x05070e },
}

/** The colour a light gives what it lights by night: firelight's warmth. Lights are white by day. */
export const NIGHT_LIGHT = 0xffe2b4

const brightnesses = Object.values(DayPhases).map(phase => phase.brightness)
/** The day cycle's darkest brightness (darkestNight): where the night look is at its fullest. */
const DARKEST = Math.min(...brightnesses)
/** Where the night's colour starts to come in (dusk); above it the darkness only lightens. */
const COLOUR_FROM = DayPhases.dusk?.brightness ?? 0.5

const LUMA = [0.2126, 0.7152, 0.0722]

type RGB = [number, number, number]

/** What the night filter needs at one brightness: all derived from the sky and the hour. */
export interface NightLook {
    /** The light map's floor (it replaces the room's darkness there): the luminance of `ambient`. */
    level: number
    /** What unlit ground is multiplied by. */
    ambient: RGB
    /** What fully lit ground is multiplied by. */
    light: RGB
    saturation: number
    lift: RGB
}

interface NightLookTuning { moonlit: NightSkyLook; moonless: NightSkyLook; light: number }

const tuning = (): NightLookTuning | undefined => {
    const w = typeof window === "undefined" ? undefined : (window as any)
    if (w?.nightLook === false) return undefined
    const live = w?.nightLook && typeof w.nightLook === "object" ? w.nightLook : {}
    return {
        moonlit: { ...NIGHT_SKIES.moonlit, ...live.moonlit },
        moonless: { ...NIGHT_SKIES.moonless, ...live.moonless },
        light: typeof live.light === "number" ? live.light : NIGHT_LIGHT,
    }
}

const rgb = (hex: number): RGB => Array.from(utils.hex2rgb(hex)) as RGB
const mix = (a: number, b: number, t: number) => a + (b - a) * t
const clamp01 = (x: number) => Math.min(1, Math.max(0, x))

/**
 * One sky's look at `brightness` (the day cycle's, 1 by day down to DARKEST). The floor eases from full daylight to
 * the sky's colour at the darkest of the night -- never darker than the plain darkness was -- and its hue, grey and
 * lift come in from dusk on, so dawn and dusk stay neutral.
 */
const skyLook = (brightness: number, look: NightSkyLook, light: number): NightLook => {
    const color = rgb(look.color)
    const floor = Math.max(1e-3, color[0] * LUMA[0] + color[1] * LUMA[1] + color[2] * LUMA[2])
    const depth = clamp01((1 - brightness) / Math.max(0.01, 1 - DARKEST))
    const x = clamp01((COLOUR_FROM - brightness) / Math.max(0.01, COLOUR_FROM - DARKEST))
    const night = x * x * (3 - 2 * x)
    const level = Math.max(brightness, mix(1, floor, depth))
    const warm = rgb(light)
    const lift = rgb(look.lift)
    return {
        level,
        ambient: color.map(c => level * mix(1, c / floor, night)) as RGB,
        light: warm.map(c => mix(1, c, night)) as RGB,
        saturation: mix(1, look.saturation, night),
        lift: lift.map(c => c * night) as RGB,
    }
}

const mixRGB = (a: RGB, b: RGB, t: number) => a.map((v, i) => mix(v, b[i], t)) as RGB

/** The look at `brightness` with `moon` (0..1) of the moon showing: the moonless look at 0, the moonlit at 1. */
export const nightLook = (brightness: number, moon: number, tune = tuning()): NightLook | undefined => {
    if (!tune) return undefined
    const m = clamp01(moon)
    const lit = skyLook(brightness, tune.moonlit, tune.light)
    if (m >= 1) return lit
    const dark = skyLook(brightness, tune.moonless, tune.light)
    if (m <= 0) return dark
    return {
        level: mix(dark.level, lit.level, m),
        ambient: mixRGB(dark.ambient, lit.ambient, m),
        light: mixRGB(dark.light, lit.light, m),
        saturation: mix(dark.saturation, lit.saturation, m),
        lift: mixRGB(dark.lift, lit.lift, m),
    }
}

/**
 * How much moon shows over a room by night: its sky (Visibility.nightSky, the moon cycle's or a pin), less the rain
 * clouds over it (`cloud`, 0..1, the Rain effect's) unless the sky is pinned. Moonlit where nothing says otherwise.
 */
export const moonShownFor = (visibility: Visibility | undefined, cloud = 0): number =>
    moonShown(visibility?.nightSky, visibility?.nightSkyPinned, cloud)

/**
 * The night look for a room, or undefined where the plain darkness stays: no darkness right now, a room without a
 * day cycle (a house or dungeon at its own fixed darkness), or a cave. `cloud` is the rain's cloud cover there.
 */
export const nightLookFor = (visibility: Visibility | undefined, dayCycle: boolean, cloud = 0): NightLook | undefined => {
    const darkness = visibility?.darkness
    if (!dayCycle || visibility?.fullOcclusion || darkness === undefined || !(darkness < 1)) return undefined
    return nightLook(darkness, moonShownFor(visibility, cloud))
}

/**
 * Global logical px -> the light map's texture coordinates, as a column-major mat3 for GLSL: the inverse of the
 * light map sprite's world transform, scaled from its texture's px to 0..1.
 */
export const screenToLightMap = (world: Matrix, width: number, height: number, out = new Float32Array(9),
    scratch = new Matrix()): Float32Array => {
    const inv = scratch.copyFrom(world).invert()
    out[0] = inv.a / width; out[1] = inv.b / height; out[2] = 0
    out[3] = inv.c / width; out[4] = inv.d / height; out[5] = 0
    out[6] = inv.tx / width; out[7] = inv.ty / height; out[8] = 1
    return out
}

// pixi's default filter vertex, also handing on where each fragment is on the screen (global logical px).
export const SCREEN_FILTER_VERTEX = `
attribute vec2 aVertexPosition;
uniform mat3 projectionMatrix;
uniform vec4 inputSize;
uniform vec4 outputFrame;
varying vec2 vTextureCoord;
varying vec2 vScreen;

void main(void) {
    vec2 position = aVertexPosition * max(outputFrame.zw, vec2(0.)) + outputFrame.xy;
    gl_Position = vec4((projectionMatrix * vec3(position, 1.0)).xy, 0.0, 1.0);
    vTextureCoord = aVertexPosition * (outputFrame.zw * inputSize.zw);
    vScreen = position;
}`

// The light map (shadow_rendering.ts) is grey: the floor everywhere, raised by lights, cut by the indoor veil.
// `lit` is how far a light raised it above the floor; `seen` what a veil left (1 where there is none).
const FRAGMENT = `
varying vec2 vTextureCoord;
varying vec2 vScreen;
uniform sampler2D uSampler;
uniform sampler2D uLightMap;
uniform mat3 uScreenToLight;
uniform float uLevel;
uniform vec3 uAmbient;
uniform vec3 uLight;
uniform vec3 uLift;
uniform float uSaturation;

void main(void) {
    vec4 world = texture2D(uSampler, vTextureCoord);
    vec2 at = clamp((uScreenToLight * vec3(vScreen, 1.0)).xy, 0.0, 1.0);
    float map = texture2D(uLightMap, at).r;
    float lit = clamp((map - uLevel) / max(1.0 - uLevel, 0.001), 0.0, 1.0);
    float seen = clamp(map / max(uLevel, 0.001), 0.0, 1.0);
    float grey = dot(world.rgb, vec3(0.2126, 0.7152, 0.0722));
    vec3 colour = mix(vec3(grey), world.rgb, mix(uSaturation, 1.0, lit));
    vec3 light = mix(uAmbient, uLight, lit);
    gl_FragColor = vec4((colour * light + uLift * world.a * (1.0 - lit)) * seen, world.a);
}`

/**
 * The world drawn through the light map in one pass: the light map's multiply (LIGHT_MAP_BLEND) plus what a blend
 * cannot do -- take colour out, which needs the channels mixed. Set on the world surface in place of the light map
 * sprite, which stays on the stage (unrendered) as the texture and placement this reads.
 */
export class NightSkyFilter extends Filter {
    private lightMap?: Sprite
    private scratch = new Matrix()

    constructor() {
        super(SCREEN_FILTER_VERTEX, FRAGMENT, {
            uLightMap: Texture.WHITE,
            uScreenToLight: new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]),
            uLevel: 1,
            uAmbient: new Float32Array([1, 1, 1]),
            uLight: new Float32Array([1, 1, 1]),
            uLift: new Float32Array([0, 0, 0]),
            uSaturation: 1,
        })
        this.padding = 0
    }

    set = (look: NightLook, lightMap: Sprite): void => {
        this.lightMap = lightMap
        const u = this.uniforms
        u.uLevel = look.level
        u.uAmbient.set(look.ambient)
        u.uLight.set(look.light)
        u.uLift.set(look.lift)
        u.uSaturation = look.saturation
    }

    apply(filterManager: FilterSystem, input: RenderTexture, output: RenderTexture, clearMode?: CLEAR_MODES): void {
        const map = this.lightMap
        if (map?.texture) {
            // Read at draw time: the transforms are this frame's by then, the map's included (it is not rendered).
            this.uniforms.uLightMap = map.texture
            screenToLightMap(map.worldTransform, map.texture.width, map.texture.height, this.uniforms.uScreenToLight, this.scratch)
        }
        filterManager.applyFilter(this, input, output, clearMode)
    }
}
